import React from "react";
import { motion } from "framer-motion";
import { 
  Play, 
  ExternalLink, 
  Clock, 
  Eye,
  ThumbsUp,
  Youtube,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface YouTubeMentionCardProps {
  /** YouTube URL */
  url?: string;
  /** Video ID */
  videoId?: string;
  /** Pre-loaded video data */
  videoData?: VideoData;
  /** Display variant */
  variant?: "inline" | "card" | "compact";
}

interface VideoData {
  id: string;
  url: string;
  title: string;
  channel: {
    name: string;
    avatar?: string;
    verified?: boolean;
  };
  thumbnail: string;
  duration?: string;
  publishedAt?: string;
  metrics?: {
    views?: number;
    likes?: number;
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatTimeAgo(dateStr: string): string {
  const delta = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(delta / 3600000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
}

function formatViews(num?: number): string {
  if (num === undefined || num === null) return "—";
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M views`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K views`;
  return `${num} views`;
}

function extractVideoId(url: string): string | null {
  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?\s]+)/,
    /youtube\.com\/shorts\/([^&?\s]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
}

function getThumbnailUrl(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline Variant
// ─────────────────────────────────────────────────────────────────────────────

const InlineVariant: React.FC<{ video: VideoData }> = ({ video }) => (
  <a
    href={video.url}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-800/60 hover:bg-gray-700/60 border border-gray-700/50 hover:border-rose-500/30 transition-all group"
  >
    <div className="relative h-5 w-8 rounded overflow-hidden flex-shrink-0">
      <img 
        src={video.thumbnail} 
        alt=""
        className="h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
        <Play className="h-2.5 w-2.5 text-white fill-white" />
      </div>
    </div>
    <span className="text-sm text-white font-medium max-w-[180px] truncate">
      {video.title}
    </span>
    <span className="text-xs text-gray-500">{video.channel.name}</span>
    <ExternalLink className="h-3 w-3 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Card Variant
// ─────────────────────────────────────────────────────────────────────────────

const CardVariant: React.FC<{ video: VideoData }> = ({ video }) => (
  <motion.div
    initial={{ opacity: 0, y: 10, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    className="relative rounded-xl border border-gray-700/50 bg-gradient-to-br from-gray-900/90 to-gray-800/90 backdrop-blur-xl overflow-hidden w-80"
  >
    {/* Thumbnail */}
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className="relative block aspect-video w-full group"
    >
      <img 
        src={video.thumbnail} 
        alt={video.title}
        className="w-full h-full object-cover"
      />
      
      {/* Play overlay */}
      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="h-14 w-14 rounded-full bg-rose-600/90 flex items-center justify-center">
          <Play className="h-7 w-7 text-white fill-white ml-1" />
        </div>
      </div>

      {/* Duration badge */}
      {video.duration && (
        <div className="absolute bottom-2 right-2 px-1.5 py-0.5 rounded bg-black/80 text-white text-xs font-medium">
          {video.duration}
        </div>
      )}

      {/* YouTube logo */}
      <div className="absolute top-2 left-2">
        <div className="h-6 w-8 rounded bg-rose-600 flex items-center justify-center">
          <Youtube className="h-4 w-4 text-white" />
        </div>
      </div>
    </a>

    {/* Content */}
    <div className="p-3">
      {/* Title */}
      <a
        href={video.url}
        target="_blank"
        rel="noopener noreferrer"
        className="block mb-2"
      >
        <h4 className="text-sm font-medium text-white line-clamp-2 hover:text-rose-300 transition-colors">
          {video.title}
        </h4>
      </a>

      {/* Channel */}
      <div className="flex items-center gap-2 mb-2">
        {video.channel.avatar ? (
          <img 
            src={video.channel.avatar} 
            alt={video.channel.name}
            className="h-6 w-6 rounded-full"
          />
        ) : (
          <div className="h-6 w-6 rounded-full bg-gray-700 flex items-center justify-center">
            <span className="text-[10px] font-bold text-gray-400">
              {video.channel.name[0]}
            </span>
          </div>
        )}
        <span className="text-xs text-gray-400 truncate">{video.channel.name}</span>
        {video.channel.verified && (
          <div className="h-3 w-3 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
            <span className="text-[8px] text-gray-300">✓</span>
          </div>
        )}
      </div>

      {/* Metrics */}
      <div className="flex items-center gap-3 text-xs text-gray-500">
        {video.metrics?.views !== undefined && (
          <div className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            <span>{formatViews(video.metrics.views)}</span>
          </div>
        )}
        {video.metrics?.likes !== undefined && (
          <div className="flex items-center gap-1">
            <ThumbsUp className="h-3 w-3" />
            <span>{(video.metrics.likes / 1000).toFixed(1)}K</span>
          </div>
        )}
        {video.publishedAt && (
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{formatTimeAgo(video.publishedAt)}</span>
          </div>
        )}
      </div>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Compact Variant
// ─────────────────────────────────────────────────────────────────────────────

const CompactVariant: React.FC<{ video: VideoData }> = ({ video }) => (
  <a
    href={video.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-start gap-3 p-2.5 rounded-lg border border-gray-700/40 bg-gray-800/40 hover:bg-gray-800/60 hover:border-rose-500/30 transition-all"
  >
    {/* Thumbnail */}
    <div className="relative w-24 aspect-video rounded overflow-hidden flex-shrink-0">
      <img 
        src={video.thumbnail} 
        alt=""
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
        <Play className="h-4 w-4 text-white fill-white" />
      </div>
      {video.duration && (
        <div className="absolute bottom-1 right-1 px-1 py-0.5 rounded bg-black/80 text-white text-[10px]">
          {video.duration}
        </div>
      )}
    </div>

    {/* Info */}
    <div className="flex-1 min-w-0">
      <h4 className="text-sm font-medium text-white line-clamp-2 mb-1">
        {video.title}
      </h4>
      <p className="text-xs text-gray-500">{video.channel.name}</p>
      {video.metrics?.views !== undefined && (
        <p className="text-xs text-gray-600 mt-0.5">
          {formatViews(video.metrics.views)}
        </p>
      )}
    </div>
  </a>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

const YouTubeMentionCard: React.FC<YouTubeMentionCardProps> = ({
  url,
  videoId,
  videoData,
  variant = "card",
}) => {
  // Derive video ID and data
  const derivedVideoId = videoId || (url ? extractVideoId(url) : null);
  
  // If we have pre-loaded data, use it
  if (videoData) {
    switch (variant) {
      case "inline":
        return <InlineVariant video={videoData} />;
      case "compact":
        return <CompactVariant video={videoData} />;
      case "card":
      default:
        return <CardVariant video={videoData} />;
    }
  }

  // Create minimal video data from URL/ID
  if (derivedVideoId) {
    const minimalData: VideoData = {
      id: derivedVideoId,
      url: url || `https://youtube.com/watch?v=${derivedVideoId}`,
      title: "YouTube Video",
      channel: { name: "YouTube" },
      thumbnail: getThumbnailUrl(derivedVideoId),
    };

    switch (variant) {
      case "inline":
        return <InlineVariant video={minimalData} />;
      case "compact":
        return <CompactVariant video={minimalData} />;
      case "card":
      default:
        return <CardVariant video={minimalData} />;
    }
  }

  // Fallback: simple link
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 border border-gray-700/40 text-rose-400 hover:text-rose-300 transition-all text-sm"
      >
        <Youtube className="h-3.5 w-3.5" />
        <span>Watch Video</span>
        <ExternalLink className="h-3 w-3" />
      </a>
    );
  }

  return null;
};

export default YouTubeMentionCard;

